<template>
  <div class="mx-4">
    <v-row>
      <!-- Aviso sobre preenchimento das notas -->
      <v-col cols="12">
        <app-alert
          icon="warning"
          message="Atenção, servidor! Utilize com moderação os extremos da escala."
          type="error"
        />
      </v-col>

      <!-- Título, status e legenda das escalas de avaliação -->
      <v-col cols="12">
        <app-title :texto="titulo" />
        <avaliacao-status
          :avaliando="prazoAtual && !concluido"
          :status="avaliacoesFinalizadas"
        />
        <legenda-escala-avaliacao :escalas="escalas" />
      </v-col>
    </v-row>

    <!-- Formulário de Avaliação -->
    <v-row v-if="prazoAtual && !concluido">
      <v-col cols="12">
        <component
          :is="avaliacaoForm"
          :avaliacoes="avaliacoes"
          :lista="lista"
          :escalas="escalas"
          :tipo="tipo"
          :perspectiva="perspectiva"
          @done="() => concluido = true"
        />
      </v-col>
    </v-row>

    <!-- Espaço para inserção de conteúdo -->
    <v-row>
      <v-col cols="12">
        <!-- Slot default -->
        <slot name="default" />
      </v-col>
    </v-row>

    <!-- Listagem dos itens avaliados -->
    <v-row v-if="!prazoFuturo">
      <v-col cols="12">
        <avaliacao-list
          :avaliacoes="avaliacoes"
          :lista="lista"
          :escalas="escalas"
          :tipo="tipo"
          :perspectiva="perspectiva"
          :disabled="!prazoAtual"
          :is-avaliacao-gestores="isAvaliacaoGestores"
        />
      </v-col>
    </v-row>

    <!-- Botão para finalizar as avaliações -->
    <v-row v-if="prazoAtual && lista.length > 0 && !avaliacoesFinalizadas" class="justify-end" no-gutters>
      <v-spacer />
      <v-tooltip :disabled="!concluido" left>
        <template v-slot:activator="{ on }">
          <v-btn
            :disabled="!concluido"
            color="secondary"
            outlined
            @click="finalizar()"
            v-on="on"
          >
            Finalizar
            <v-icon class="ml-2">check</v-icon>
          </v-btn>
        </template>
        <span>Clique no botão para finalizar a avaliação</span>
      </v-tooltip>
    </v-row>

    <!-- Dialog para exibir a mensagem de agradecimento após as avaliações -->
    <dialog-agradecimento
      :descricao="dialogAgradecimento.descricao"
      :dialog-visible="dialogAgradecimento.visibilidade"
      :toogle-visible="toogleDialogAgradecimento"
    />
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import AppAlert from '@/components/core/AppAlert';
import AppTitle from '@/components/core/AppTitle';
import AvaliacaoStatus from '@/components/diagnostico/AvaliacaoStatus';
import LegendaEscalaAvaliacao from '@/components/diagnostico/LegendaEscalaAvaliacao';
import AvaliacaoForm from '@/components/diagnostico/AvaliacaoForm';
import AvaliacaoFormGestor from '@/components/diagnostico/AvaliacaoFormGestor';
import AvaliacaoList from '@/components/diagnostico/AvaliacaoList';
import DialogAgradecimento from '@/components/DialogAgradecimento';
import Data from '@/modules/data';

const avaliacaoRepository = RepositoryFactory.get('avaliacao');

export default {
  name: 'Avaliacao',

  components: {
    AppTitle,
    AvaliacaoStatus,
    LegendaEscalaAvaliacao,
    AvaliacaoForm,
    AvaliacaoFormGestor,
    AvaliacaoList,
    DialogAgradecimento,
    AppAlert,
  },

  props: {
    titulo: {
      type: String,
      required: false,
      default: 'AVALIAÇÃO',
    },
    diagnostico: {
      type: Object,
      required: true,
    },
    tipo: {
      type: String,
      required: true,
    },
    perspectiva: {
      type: String,
      required: true,
    },
    isAvaliacaoGestores: {
      type: Boolean,
      required: false,
      default: false,
    },
    tutorial: {
      type: Object,
      required: true,
    },
    inicio: {
      type: String,
      required: true,
    },
    fim: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      dialogAgradecimento: {
        descricao: 'Agradecemos pela sua colaboração! Você ainda poderá editar a avaliação até o período estabelecido.',
        visibilidade: false,
      },
      concluido: false,
    };
  },

  computed: {
    avaliacoes() {
      const avaliacoesProp = this.tipo.toLowerCase() + this.perspectiva[0].toUpperCase()
        + this.perspectiva.substring(1).toLowerCase();

      // As avaliações desse tipo estão juntas (Gestor e Servidor), mas devem ser avaliadas separadamente
      if (this.tipo === 'SUBORDINADO' && this.perspectiva === 'RESPONSABILIDADE') {
        if (this.isAvaliacaoGestores) {
          // Somente gestores
          return this.$store.state.diagnostico[avaliacoesProp]
            .filter((a) => a.avaliado.unidadeVersao.id !== a.avaliador.unidadeVersao.id)
            .sort((a, b) => a.avaliado.nome.localeCompare(b.avaliado.nome));
        }

        // Somente servidores
        return this.$store.state.diagnostico[avaliacoesProp]
          .filter((a) => a.avaliado.unidadeVersao.id === a.avaliador.unidadeVersao.id)
          .sort((a, b) => a.avaliado.nome.localeCompare(b.avaliado.nome));
      }

      return this.$store.state.diagnostico[avaliacoesProp]
        .map((a) => a)
        .sort((a, b) => a.avaliado.nome.localeCompare(b.avaliado.nome));
    },
    lista() {
      if (this.perspectiva === 'COMPORTAMENTAL') {
        return this.$store.state.diagnostico.comportamentos.map((c) => c)
          .sort((a, b) => a.descricaoAtualizada.localeCompare(b.descricaoAtualizada));
      }
      if (this.perspectiva === 'RESPONSABILIDADE') {
        // Retorna as responsabilidades conforme as avaliações selecionadas
        return this.$store.state.diagnostico.responsabilidades
          .filter((r) => this.avaliacoes.some((a) => a.avaliado.unidadeVersao.id === r.unidade.id))
          .sort((a, b) => a.tituloAtual.localeCompare(b.tituloAtual));
      }
      return [];
    },
    escalas() {
      return this.$store.state.diagnostico.escalas[this.perspectiva.toLocaleLowerCase()];
    },
    isTutorialExibido() {
      let tutorialProp;

      if (this.tutorial.nomeGenerico) {
        tutorialProp = this.tutorial.nomeGenerico[0].toLowerCase() + this.tutorial.nomeGenerico.substring(1);
      } else {
        tutorialProp = this.tutorial.nome[0].toLowerCase() + this.tutorial.nome.substring(1);
      }

      return this.$store.state.tutorial.dados[tutorialProp];
    },
    avaliacaoForm() {
      return this.isAvaliacaoGestores ? 'AvaliacaoFormGestor' : 'AvaliacaoForm';
    },
    avaliacoesFinalizadas() {
      return this.avaliacoes.every((a) => a.finalizada);
    },
    prazoEsgotado() {
      return Data.compare('isAfter', { b: this.fim });
    },
    prazoFuturo() {
      return Data.compare('isBefore', { b: this.inicio });
    },
    prazoAtual() {
      return !this.prazoEsgotado && !this.prazoFuturo;
    },
  },

  created() {
    if (!this.isTutorialExibido && this.prazoAtual) {
      this.$store.dispatch('app/showTutorial', { ...this.tutorial });
    }
  },

  methods: {
    finalizar() {
      if (this.concluido) {
        const avaliacoesIds = this.avaliacoes.map((a) => a.id);

        avaliacaoRepository.finalizar(avaliacoesIds).then((response) => {
          const avaliacoes = response.data;
          const avaliacoesProp = this.tipo.toLowerCase() + this.perspectiva[0].toUpperCase()
            + this.perspectiva.substring(1).toLowerCase();

          const payload = {
            tipo: this.tipo,
            perspectiva: this.perspectiva,
            // As avaliações técnicas da equipe são finalizadas parcialmente (Gestores/Servidores)
            avaliacao: [
              ...avaliacoes,
              ...this.$store.state.diagnostico[avaliacoesProp].filter((a) => !avaliacoes.some((a2) => a2.id === a.id)),
            ],
          };

          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          this.$store.dispatch('diagnostico/setAvaliacao', payload);
          this.toogleDialogAgradecimento();
        });
      }
    },
    toogleDialogAgradecimento() {
      this.dialogAgradecimento.visibilidade = !this.dialogAgradecimento.visibilidade;
    },
  },
};
</script>
